import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type {
  IntegrationConfigRead,
  ProviderConfig,
} from "@/lib/types/itsm-config";
import type {
  FieldImportResult,
  ImportableFields,
  ImportTarget,
} from "@/lib/itsm-config/field-import";

// Served by itsm-api on its internal router; reachable only through the
// backoffice proxy (ENG-8760).
const INTERNAL = "/proxy/itsm-api/api/v1/internal";

export type ImportProvider = "jira" | "monday";

/** The Jira project's custom fields and what an import makes of each. */
export function useJiraImportableFields(
  tenantId: string,
  credentialId: string,
  projectKey: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["field-import", "jira", tenantId, credentialId, projectKey],
    queryFn: async () => {
      const { data } = await backofficeClient.get<ImportableFields>(
        `${INTERNAL}/metadata/jira/projects/${encodeURIComponent(projectKey)}/importable-fields`,
        { params: { tenant_id: tenantId, integration_id: credentialId } },
      );
      return data;
    },
    enabled: enabled && Boolean(tenantId && credentialId && projectKey),
    retry: false,
  });
}

/** The monday board's columns and what an import makes of each. */
export function useMondayImportableColumns(
  tenantId: string,
  credentialId: string,
  boardId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["field-import", "monday", tenantId, credentialId, boardId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<ImportableFields>(
        `${INTERNAL}/metadata/monday/boards/${encodeURIComponent(boardId)}/importable-columns`,
        { params: { tenant_id: tenantId, integration_id: credentialId } },
      );
      return data;
    },
    enabled: enabled && Boolean(tenantId && credentialId && boardId),
    retry: false,
  });
}

export interface FieldImportRequest {
  target: ImportTarget;
  desk_id: string | null;
  /** Jira: the project; monday: unused. */
  project_key?: string;
  /** monday: the board; Jira: unused. */
  board_id?: string;
  ids: string[];
}

/**
 * Create Harmony fields from provider fields and pair them. The backend
 * saves the new fields and the integration's mappings itself; the caller
 * takes the mappings into its draft.
 */
export function useImportProviderFields(
  tenantId: string,
  credentialId: string,
  provider: ImportProvider,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: FieldImportRequest) => {
      const body =
        provider === "jira"
          ? {
              target: request.target,
              desk_id: request.desk_id,
              project_key: request.project_key,
              field_ids: request.ids,
            }
          : {
              target: request.target,
              desk_id: request.desk_id,
              board_id: request.board_id,
              column_ids: request.ids,
            };
      const { data } = await backofficeClient.post<FieldImportResult>(
        `${INTERNAL}/${provider}/field-imports`,
        body,
        { params: { tenant_id: tenantId, integration_id: credentialId } },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["field-import"] });
      void queryClient.invalidateQueries({
        queryKey: ["service-desk", "custom-field-config", tenantId],
      });
    },
  });
}

/**
 * The provider config as stored. Read after an import whose answer was lost:
 * the backend may have saved the mappings anyway, and the editor must not
 * save over them.
 */
export async function readStoredProviderConfig(
  tenantId: string,
  credentialId: string,
): Promise<ProviderConfig | null> {
  const { data } = await backofficeClient.get<IntegrationConfigRead>(
    `/proxy/integrations/api/v1/credentials/${credentialId}/config`,
    { params: { tenant_id: tenantId } },
  );
  return data.config.provider_config ?? null;
}
